import { useEffect, useState, type FormEvent } from 'react';
import { useTranslation } from 'react-i18next';
import { useBookingAvailability } from '../../../hooks/useBookingAvailability';
import { BookingStepper } from '../BookingStepper/BookingStepper';
import { BookingStepWhen } from '../BookingStepWhen/BookingStepWhen';
import { BookingStepDetails } from '../BookingStepDetails/BookingStepDetails';
import { BookingStepDone } from '../BookingStepDone/BookingStepDone';
import type { ConsultationService } from '../../../types';
import { Modal } from '../../common/Modal/Modal';

import './BookingModal.css';

interface BookingModalProps {
  service: ConsultationService | null;
  isOpen: boolean;
  onClose: () => void;
}

type Step = 'when' | 'details' | 'done';

function BookingModal({ service, isOpen, onClose }: BookingModalProps) {
  const { t, i18n } = useTranslation();
  const isRtl = i18n.resolvedLanguage?.startsWith('ar') ?? false;
  const locale = isRtl ? 'ar' : 'en';

  const [step, setStep] = useState<Step>('when');

  const {
    form,
    slots,
    loadingSlots,
    slotsError,
    loadingDays,
    submitting,
    submitError,
    result,
    disabledMatcher,
    handleDateSelect,
    handleSlotPick,
    handleChange,
    submitBooking,
  } = useBookingAvailability(service, isOpen);

  useEffect(() => {
    if (isOpen) {
      setStep('when');
    }
  }, [isOpen, service]);

  if (!isOpen || !service) {
    return null;
  }

  const handleContinue = () => {
    if (!form.selectedSlot) return;
    setStep('details');
  };

  const handleSubmit = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    const success = await submitBooking(service.id, service.title, locale);
    if (success) {
      setStep('done');
    }
  };

  return (
    <Modal isOpen={isOpen} onClose={onClose} className="booking-modal">
      <BookingStepper step={step} />

      {step !== 'done' && (
        <div className="booking-modal-header">
          <div>
            <p className="booking-modal-eyebrow">{t('booking.summaryLabel')}</p>
            <h2 id="booking-modal-title" className="booking-modal-title">
              {t('booking.title', { service: service.title })}
            </h2>
            <p className="booking-modal-subtitle">{t('booking.subtitle')}</p>
          </div>
          <div className="booking-service-thumb">
            <img src={service.image} alt={service.title} />
          </div>
        </div>
      )}

      {step === 'when' && (
        <BookingStepWhen
          selectedDate={form.selectedDate}
          selectedSlot={form.selectedSlot}
          slots={slots}
          loadingSlots={loadingSlots}
          slotsError={slotsError}
          loadingDays={loadingDays}
          disabledMatcher={disabledMatcher}
          handleDateSelect={handleDateSelect}
          handleSlotPick={handleSlotPick}
          onClose={onClose}
          onContinue={handleContinue}
        />
      )}

      {step === 'details' && (
        <BookingStepDetails
          service={service}
          form={form}
          submitting={submitting}
          submitError={submitError}
          handleChange={handleChange}
          onBack={() => setStep('when')}
          onSubmit={handleSubmit}
        />
      )}

      {step === 'done' && result && (
        <BookingStepDone
          service={service}
          result={result}
          onClose={onClose}
        />
      )}
    </Modal>
  );
}

export default BookingModal;
